import React from 'react';
import { AlertTriangle, RefreshCw, Server, Wifi, Database, Code, Lock, FileWarning } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ErrorScreenProps {
  error?: Error | null;
  code?: string;
  message?: string;
  onReset?: () => void;
}

const ErrorScreen: React.FC<ErrorScreenProps> = ({ 
  error, 
  code = 'ERROR', 
  message, 
  onReset 
}) => {
  // Determine error type and customize the display
  const errorMessage = message || error?.message || 'Something went wrong';
  const errorCode = code || getErrorCode(errorMessage);
  const { icon: ErrorIcon, title, description, color } = getErrorDetails(errorCode, errorMessage);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-md w-full transform transition-all duration-500 hover:shadow-2xl">
        {/* Error header with gradient background */}
        <div className={`p-6 ${color.bg}`}>
          <div className="flex justify-center">
            <div className={`w-16 h-16 rounded-full ${color.iconBg} flex items-center justify-center`}>
              <ErrorIcon className={`h-8 w-8 ${color.icon}`} />
            </div>
          </div>
          <h2 className="mt-4 text-center text-2xl font-bold text-white">{title}</h2>
        </div>
        
        {/* Error details */}
        <div className="p-6">
          <div className="mb-6">
            <div className="text-center text-gray-600 mb-4">
              {description || errorMessage}
            </div>
            
            {/* Error code display */}
            <div className="bg-gray-100 rounded-lg p-3 font-mono text-sm text-center text-gray-700">
              Error Code: {errorCode}
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex flex-col space-y-3">
            {onReset && (
              <button
                onClick={onReset}
                className={`w-full py-3 rounded-lg font-medium transition flex items-center justify-center space-x-2 ${color.button} ${color.buttonHover}`}
              >
                <RefreshCw className="h-5 w-5" />
                <span>Try Again</span>
              </button>
            )}
            
            <Link
              to="/"
              className="w-full py-3 bg-gray-200 text-gray-800 rounded-lg font-medium transition hover:bg-gray-300 flex items-center justify-center space-x-2"
            >
              <span>Return to Home</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to determine error code from message
function getErrorCode(message: string): string {
  if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
    return 'NETWORK_ERROR';
  }
  if (message.includes('authentication') || message.includes('auth') || message.includes('login')) {
    return 'AUTH_ERROR';
  }
  if (message.includes('permission') || message.includes('access') || message.includes('forbidden')) {
    return 'PERMISSION_ERROR';
  }
  if (message.includes('not found') || message.includes('404')) {
    return 'NOT_FOUND';
  }
  if (message.includes('server') || message.includes('500')) {
    return 'SERVER_ERROR';
  }
  if (message.includes('database') || message.includes('query')) {
    return 'DATABASE_ERROR';
  }
  return 'UNKNOWN_ERROR';
}

// Helper function to get error details based on code
function getErrorDetails(code: string, message: string) {
  switch (code) {
    case 'NETWORK_ERROR':
      return {
        icon: Wifi,
        title: 'Connection Error',
        description: 'Unable to connect to the server. Please check your internet connection.',
        color: {
          bg: 'bg-gradient-to-r from-blue-500 to-blue-600',
          icon: 'text-blue-500',
          iconBg: 'bg-white',
          button: 'bg-blue-500 text-white',
          buttonHover: 'hover:bg-blue-600'
        }
      };
    case 'AUTH_ERROR':
      return {
        icon: Lock,
        title: 'Authentication Error',
        description: 'Your session may have expired. Please sign in again.',
        color: {
          bg: 'bg-gradient-to-r from-purple-500 to-purple-600',
          icon: 'text-purple-500',
          iconBg: 'bg-white',
          button: 'bg-purple-500 text-white',
          buttonHover: 'hover:bg-purple-600'
        }
      };
    case 'PERMISSION_ERROR':
      return {
        icon: Lock,
        title: 'Access Denied',
        description: 'You don\'t have permission to access this resource.',
        color: {
          bg: 'bg-gradient-to-r from-red-500 to-red-600',
          icon: 'text-red-500',
          iconBg: 'bg-white',
          button: 'bg-red-500 text-white',
          buttonHover: 'hover:bg-red-600'
        }
      };
    case 'NOT_FOUND':
      return {
        icon: FileWarning,
        title: 'Not Found',
        description: 'The resource you\'re looking for doesn\'t exist or has been moved.',
        color: {
          bg: 'bg-gradient-to-r from-gray-700 to-gray-800',
          icon: 'text-gray-700',
          iconBg: 'bg-white',
          button: 'bg-gray-700 text-white',
          buttonHover: 'hover:bg-gray-800'
        }
      };
    case 'SERVER_ERROR':
      return {
        icon: Server,
        title: 'Server Error',
        description: 'Something went wrong on our servers. We\'re working to fix it.',
        color: {
          bg: 'bg-gradient-to-r from-red-500 to-red-600',
          icon: 'text-red-500',
          iconBg: 'bg-white',
          button: 'bg-red-500 text-white',
          buttonHover: 'hover:bg-red-600'
        }
      };
    case 'DATABASE_ERROR':
      return {
        icon: Database,
        title: 'Database Error',
        description: 'There was an issue with our database. Please try again later.',
        color: {
          bg: 'bg-gradient-to-r from-yellow-500 to-yellow-600',
          icon: 'text-yellow-500',
          iconBg: 'bg-white',
          button: 'bg-yellow-500 text-white',
          buttonHover: 'hover:bg-yellow-600'
        }
      };
    case 'VALIDATION_ERROR':
      return {
        icon: AlertTriangle,
        title: 'Validation Error',
        description: 'The data you provided is invalid. Please check your inputs.',
        color: {
          bg: 'bg-gradient-to-r from-amber-500 to-amber-600',
          icon: 'text-amber-500',
          iconBg: 'bg-white',
          button: 'bg-amber-500 text-white',
          buttonHover: 'hover:bg-amber-600'
        }
      };
    case 'RENDER_ERROR':
      return {
        icon: Code,
        title: 'Rendering Error',
        description: 'There was a problem displaying this page. Please try refreshing.',
        color: {
          bg: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
          icon: 'text-indigo-500',
          iconBg: 'bg-white',
          button: 'bg-indigo-500 text-white',
          buttonHover: 'hover:bg-indigo-600'
        }
      };
    default:
      return {
        icon: AlertTriangle,
        title: 'Unexpected Error',
        description: message || 'Something went wrong. Please try again later.',
        color: {
          bg: 'bg-gradient-to-r from-orange-500 to-orange-600',
          icon: 'text-orange-500',
          iconBg: 'bg-white',
          button: 'bg-orange-500 text-white',
          buttonHover: 'hover:bg-orange-600'
        }
      };
  }
}

export default ErrorScreen;